import { useCallback, useState } from 'react';
import type { AudienceViewMode } from '../../../../model';

export const AUDIENCE_VIEW_MODE_KEY = 'audiences.viewMode';

const DEFAULT_VIEW_MODE: AudienceViewMode = 'grid';

const isViewMode = (value: string | null): value is AudienceViewMode =>
  value === 'grid' || value === 'table';

/**
 * The list's grid-or-table choice, remembered across navigation.
 *
 * Reads storage once through a lazy initialiser and writes on change, so there
 * is no effect syncing the two. Storage failures (private mode, quota) are
 * swallowed: losing the preference is acceptable, refusing to switch the view is
 * not.
 */
export function useAudienceViewMode() {
  const [viewMode, setViewModeState] = useState<AudienceViewMode>(() => {
    try {
      const stored = localStorage.getItem(AUDIENCE_VIEW_MODE_KEY);
      return isViewMode(stored) ? stored : DEFAULT_VIEW_MODE;
    } catch {
      return DEFAULT_VIEW_MODE;
    }
  });

  const setViewMode = useCallback((next: AudienceViewMode) => {
    setViewModeState(next);
    try {
      localStorage.setItem(AUDIENCE_VIEW_MODE_KEY, next);
    } catch {
      // Preference not persisted; the view still changed for this session.
    }
  }, []);

  return { viewMode, setViewMode };
}
